import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSpinner } from 'react-icons/fa';
import back from 'assets/icons/back.svg';

import { LoginOutcome, MfaChallenge } from 'config/axiosInstance';
import { classifyMfaError, MfaFailure } from './mfaErrors';
import { useCountdown } from './useCountdown';

/** 各段の画面が受け取るもの。 */
export type MfaStepProps = {
  challenge: MfaChallenge;
  email: string;
  /** 次の段へ（mfa_token は段ごとに替わる）。 */
  onNext: (challenge: MfaChallenge) => void;
  /** パスワードの画面へ戻す。message は login の名前空間の鍵（無ければ null）。 */
  onRestart: (message: string | null) => void;
  /** トークンを受け取って確かめ終えた。 */
  onSignedIn: (outcome: LoginOutcome) => void;
};

type FrameProps = {
  title: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  onBack: () => void;
  /**
   * 送信中は戻れない（#385 の Codex の指摘 4368093977）。戻ると、送った要求の結果（失敗でのやり直し・
   * 成功でのログイン）が、その後に始めた別のログインに当たってしまうため。
   */
  backDisabled?: boolean;
};

/**
 * 2段階認証の各段の枠。見出し・説明・運営の注記（「ログイン状態を保持する」が効かない）・
 * パスワードの画面へ戻るボタン。段が替わったら見出しへフォーカスを移す（読み上げで段の替わりを伝える）。
 */
export const MfaFrame: React.FC<FrameProps> = ({ title, subtitle, children, onBack, backDisabled }) => {
  const { t } = useTranslation('login');
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, [title]);

  return (
    // 上の余白は、画面の上に固定したロゴと言語の切り替え（Login/index.tsx。下端は 96px）に重ならないため。
    // 認証アプリの登録の段は縦に長く、余白が無いと見出しがロゴの下に隠れた。
    <div className="flex flex-col gap-6 md:gap-8 w-full mx-20 pt-28 pb-8">
      <div className="flex flex-col gap-2">
        <h1 ref={headingRef} tabIndex={-1} className="text-h5 font-bold focus:outline-none">
          {title}
        </h1>
        {subtitle ? <p className="text-subtitle-1 text-neutral/100">{subtitle}</p> : null}
      </div>
      {/* PR5a。運営のセッションは「ログイン状態を保持する」によらず、ログインから1日で切れる（#384）。 */}
      <p
        data-testid="operator-keep-note"
        className="bg-warning/[0.1] text-neutral/300 px-4 py-3 rounded-lg text-body-2"
      >
        {t('mfa.operator-note')}
      </p>
      {children}
      <button
        type="button"
        className={`flex items-center gap-2 self-center ${backDisabled ? 'cursor-not-allowed' : ''}`}
        onClick={onBack}
        disabled={backDisabled}
      >
        <img src={back} alt="" />
        <span className="text-subtitle-1 text-neutral/300">{t('password-reset.back')}</span>
      </button>
    </div>
  );
};

/**
 * 試行ごとの AbortController（#385 の Codex の指摘 4368093977）。begin() で新しい試行を始め、前の試行と、
 * 段の画面が消えたとき（戻る・次の段・別のログイン）の試行を abort する。abort した試行の結果は使わない
 * （API の層は最後の段のトークンを保存せず、Cookie を失効させる）。
 */
export function useAttempt() {
  const current = useRef<AbortController | null>(null);
  useEffect(() => () => current.current?.abort(), []);
  return useCallback(() => {
    current.current?.abort();
    const controller = new AbortController();
    current.current = controller;
    return controller.signal;
  }, []);
}

/** 送る・待つボタン（ログインの画面と同じ見た目）。 */
export const SubmitButton: React.FC<{ busy: boolean; disabled?: boolean; label: string }> = ({
  busy,
  disabled,
  label,
}) => {
  const { t } = useTranslation('login');
  const off = busy || Boolean(disabled);
  return (
    <button
      type="submit"
      className={`flex w-full h-16 rounded-lg justify-center items-center ${
        off ? 'bg-neutral/50 cursor-not-allowed' : 'bg-primary/300'
      } text-title text-white`}
      disabled={off}
    >
      {busy ? (
        <>
          <FaSpinner className="animate-spin mr-2" aria-hidden="true" />
          {t('signing-in')}
        </>
      ) : (
        label
      )}
    </button>
  );
};

/** 誤りの表示（role=alert）。 */
export const ErrorBox: React.FC<{ id: string; text: string }> = ({ id, text }) =>
  text ? (
    // 2026-10-25 axe（color-contrast）。text-error（#de0000）は bg-error/[0.1] の上で 4.5 に届かない。既存の警告
    // の配色（枠 red-300・地 red-50・文字 red-700。白地で 4.5 以上。ログインの画面と同じ）にした。
    <div id={id} role="alert" className="border border-red-300 bg-red-50 text-red-700 px-4 py-3 rounded-lg">
      <span className="block sm:inline">{text}</span>
    </div>
  ) : null;

/**
 * 段の中の失敗の扱い。パスワードの画面へ戻すもの（401・409 など）は onRestart、
 * それ以外（コードの誤り・待つ・通信）はこの段の中で文を出す。待つ間は送れない。
 * handled に無いもの（送り直しの 429）は呼び出し側へ返す。
 */
export function useStepFailure(onRestart: (message: string | null) => void) {
  const { t } = useTranslation('login');
  const [text, setText] = useState('');
  const [throttled, setThrottled] = useState(false);
  const [waitLeft, startWait] = useCountdown();

  const handle = useCallback(
    (error: unknown): MfaFailure | null => {
      const failure = classifyMfaError(error);
      setThrottled(false);
      switch (failure.kind) {
        case 'aborted':
          return null;
        case 'restart':
          onRestart(failure.message);
          return null;
        case 'code_invalid':
          setText(t('mfa.error-code-invalid'));
          return null;
        case 'throttled':
          if (failure.seconds) {
            startWait(failure.seconds);
            setThrottled(true);
            setText('');
          } else {
            setText(t('error_throttled'));
          }
          return null;
        case 'password_rejected':
          setText(failure.messages.join(' ') || t('mfa.error-password-rejected'));
          return null;
        case 'network':
          setText(t('error_network'));
          return null;
        case 'origin_not_allowed':
          setText(t('error_origin_not_allowed'));
          return null;
        default:
          return failure;
      }
    },
    [onRestart, startWait, t]
  );

  const shown =
    throttled && waitLeft > 0 ? t('mfa.error-throttled-seconds', { seconds: waitLeft }) : text;
  return { text: shown, setText, waiting: throttled && waitLeft > 0, handle };
}
